import React, { FunctionComponent } from 'react';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useQuery } from "@tanstack/react-query";
import { useAtom } from 'jotai';
import { useTranslation } from 'react-i18next'

import { fetch_all_with_count } from "../../../../lib/v31lib";
import { loginAtom } from '../../../../lib/auth';
import { currentPracticeAtom } from '../../../../lib/practice';

import VisibilityIcon from '@mui/icons-material/Visibility';

import {
  Divider,
  Grid,
  Paper,
} from '@mui/material';

import {
  Button,
  IconButton,
  Typography
} from '../../../../components/v2/styled';

import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';

import { User } from '../../../../models/User';

const { DateTime } = require("luxon");

import HelpersClientWidgets from '../../../../actions/helpers/client_widgets';

type AdviceUploadRow = {
  id: number;
  get_item?: string;
  item_file_name?: string;
  date?: string;
  created_at?: string;
  is_hidden?: boolean;
}

type Props = {
  object: User;
}

export const CoachUserWidgetsBasicAdviceUploads: FunctionComponent<Props> = ({
  object
}) => {
  const [login, setLogin] = useAtom(loginAtom);
  const [currentPractice, setCurrentPractice] = useAtom(currentPracticeAtom);
  const theme = useTheme();
  const navigate = useNavigate();
  const {t, i18n} = useTranslation(['translations']);

  // Pre-format dates as strings (Luxon, same output as <Dt />) so static +
  // value can live in ONE Typography string child — required for the html
  // attribute to engage (it only applies to single-string children).
  // Upload dates are stored as dd/LL/y (the DatePicker's format), older rows
  // can carry ISO — hence the hyphen split before parsing.
  const fmtDay = (d?: string) => {
    if (!d) return '';
    const dt = d.indexOf('-') > -1 ? DateTime.fromISO(d) : DateTime.fromFormat(d, 'dd/LL/y');
    return dt.setZone("Europe/Brussels").toFormat("DDD", {locale: i18n.resolvedLanguage || 'nl'});
  }
  const fmtTime = (iso?: string) => {
    return !!iso ? DateTime.fromISO(iso).setZone("Europe/Brussels").toFormat("t") : '';
  }
  const fmtDateTime = (date?: string, created_at?: string) => {
    return [fmtDay(date), fmtTime(created_at)].filter(Boolean).join(' ');
  }

  let totalCount: number | undefined;

  // Coach-level widget settings: composite { cta, counts, list } under one
  // value key. Stored on login.settings.userdetails_widgets.settings.
  const widgetSettings = login?.settings?.userdetail_widgets?.settings;
  const cfg = widgetSettings?.[HelpersClientWidgets.basicAdviceUploadsValueKey];
  const showCta = HelpersClientWidgets.getBasicAdviceUploadsCtaEnabled(cfg);
  const showCounts = HelpersClientWidgets.getBasicAdviceUploadsCountsEnabled(cfg);
  const showList = HelpersClientWidgets.getBasicAdviceUploadsListEnabled(cfg);

  // Newest 5 uploads in the uploads tab's exact order (date + created_at both
  // selected so each row can show the date AND the upload time). `d.count` is
  // the total regardless of per_page.
  const {
    data: uploads,
    isSuccess: uploadsSuccess,
  } = useQuery({
    queryKey: ["client", "advice_uploads_widget", object?.id],
    queryFn: () =>
      fetch_all_with_count<AdviceUploadRow>(
        `/${i18n.resolvedLanguage || 'nl'}/v3/objects/fetch_all`,
        {
          object: 'advice_upload',
          fields: ['id', 'get_item', 'item_file_name', 'date', 'created_at', 'is_hidden'],
          order: 'date DESC, created_at DESC',
          per_page: 5,
          page: 0,
          filter: {
            search: '',
            advanced: {
              user_id: object?.id,
              practice_id: currentPractice?.id
            }
          }
        },
        login
      ),
      select: (d) => {
        totalCount = d.count;
        return d.result
      },
    enabled: !!object?.id && !!currentPractice?.id,
  });

  const rows = uploads || [];
  const count = totalCount !== undefined ? totalCount : (object?.advice_uploads_count || 0);

  return <Paper sx={{padding: 2, marginTop: 1, position: 'relative'}}>
    {/* CTA: small text button top-right, absolute in the title row. Gated by
        the coach's `cta` setting. No premium lock, the file page's tabs gate
        their own modules. */}
    {showCta && <Button
      label={t("coach.general.actions.to_advice_uploads")}
      id="goto_advice_uploads"
      text
      size="small"
      startIcon={<ArrowRightAltIcon />}
      sx={{position: 'absolute', top: 4, right: 4, marginTop: 0, zIndex: 2}}
      onClick={() => {
        navigate(`/user/${object.id}/file`, {state: {tab: 3}});
      }}
    />}

    <Grid container spacing={1}>
      <Grid item xs={12}>
        <Typography variant="h5" html>{t("coach.submenu.detail.basic_advice_uploads")}</Typography>
        <Divider />
      </Grid>

      {/* Summary counts: one i18n plural pair, only meaningful when at least
          one upload exists. */}
      {showCounts && count > 0 && <Grid item xs={12}>
        <Typography variant="body2" html>
          {t("coach.submenu.detail.advice_uploads_count", {count: count})}
        </Typography>
      </Grid>}

      {/* Upload rows: file name + date/time, a Divider between consecutive
          rows. Toggled by the show_list setting (the counts line stays
          regardless). */}
      {showList && rows.map((row, idx) => <React.Fragment key={row.id}>
        <Grid item xs={12} sx={{display: 'flex', alignItems: 'center', gap: 1}}>
          <Grid container spacing={0} sx={{flex: 1, minWidth: 0}}>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>{row.item_file_name || '-'}</Typography>
              <Typography variant="caption" sx={{color: 'text.secondary'}}>{fmtDateTime(row.date, row.created_at)}</Typography>
            </Grid>
          </Grid>
          {!!row.get_item && <IconButton
            title={"View"}
            sx={{padding: 0.5}}
            onClick={() => {
              window.open(row.get_item, "_BLANK");
            }}
          >
            <VisibilityIcon sx={{fontSize: '1.1rem'}} />
          </IconButton>}
        </Grid>
        {idx < rows.length - 1 && <Grid item xs={12}>
          <Divider />
        </Grid>}
      </React.Fragment>)}

      {/* Empty state: query succeeded, nothing there, and the counts line is
          the body that would have shown it. */}
      {uploadsSuccess && count === 0 && rows.length === 0 && showCounts && <Grid item xs={12}>
        <Typography sx={{color: theme.palette.secondary.main, fontSize: '0.7rem', fontStyle: 'italic'}} html>{t("general.no_results_found")}</Typography>
      </Grid>}

    </Grid>
  </Paper>;
}
